import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import api from '@/lib/api';
import useSchool, { BRAND_COLOR_FALLBACK } from '@/hooks/useSchool';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, RefreshCw, Loader2, MessageSquare, Handshake, Users } from 'lucide-react';
import StudentNeedsHelpSheet from '@/components/teacher/StudentNeedsHelpSheet';
import ClassWideGapsRollup from '@/components/teacher/ClassWideGapsRollup';

const STATUS_PILL = {
  not_started: { label: 'Not started', cls: 'bg-slate-100 text-slate-600 border-slate-300' },
  in_progress: { label: 'In progress', cls: 'bg-blue-100 text-blue-700 border-blue-300' },
  submitted: { label: 'Awaiting review', cls: 'bg-amber-100 text-amber-700 border-amber-300' },
  released: { label: 'Released', cls: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
};

export default function SchoolTeacherAssessmentStatus() {
  const navigate = useNavigate();
  const { aid } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState(null);
  const school = useSchool(me?.school_id);

  // Needs-help feeds for this assessment
  const [needsHelpByStudent, setNeedsHelpByStudent] = useState([]);
  const [classWideGroups, setClassWideGroups] = useState([]);

  // Per-student sheet state
  const [sheetOpen, setSheetOpen] = useState(false);
  const [activeStudent, setActiveStudent] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/teacher/me/assessments/${aid}/status`);
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load status');
    } finally { setLoading(false); }
  }, [aid]);

  const loadNeedsHelp = useCallback(async () => {
    try {
      const [perStudent, classWide] = await Promise.all([
        api.get(`/teacher/me/assessments/${aid}/needs-help-by-student`),
        api.get('/teacher/me/alerts/needs-help', { params: { assessment_id: aid } }),
      ]);
      setNeedsHelpByStudent(perStudent.data?.students || []);
      setClassWideGroups(classWide.data?.groups || []);
    } catch {
      // Silently ignore — needs-help is supplementary; status is the primary view.
    }
  }, [aid]);

  useEffect(() => {
    (async () => {
      try {
        const meRes = await api.get('/auth/me');
        setMe(meRes.data);
      } catch {/* */}
    })();
    load();
    loadNeedsHelp();
  }, [load, loadNeedsHelp]);

  const brandColor = school?.brand_color || BRAND_COLOR_FALLBACK;
  const a = data?.assessment;
  const rows = data?.rows || [];

  const counts = rows.reduce(
    (acc, r) => { acc[r.status] = (acc[r.status] || 0) + 1; return acc; },
    {}
  );

  // Map enrollment_id -> needs-help student record for fast badge lookup.
  const needsHelpByEnrollment = useMemo(() => {
    const m = {};
    for (const s of needsHelpByStudent) {
      if (s.enrollment_id) m[s.enrollment_id] = s;
    }
    return m;
  }, [needsHelpByStudent]);

  const audienceLabel = a?.audience_summary || null;
  const isTargeted = Array.isArray(a?.assigned_to_enrollment_ids) && a.assigned_to_enrollment_ids.length > 0;

  const openSheetForStudent = (student) => {
    setActiveStudent(student);
    setSheetOpen(true);
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50"
      data-testid="school-teacher-assessment-status"
    >
      <header
        className="border-b bg-white/90 backdrop-blur sticky top-0 z-10"
        style={{ borderTopWidth: 4, borderTopColor: brandColor }}
      >
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center gap-3 flex-wrap">
          <Button variant="ghost" size="sm" onClick={() => navigate('/school-teacher/assessments')} data-testid="back-btn">
            <ArrowLeft size={16} className="mr-1.5" /> Back
          </Button>
          <div className="flex-1">
            <h1 className="font-bold text-slate-900" data-testid="status-title">
              {a?.title || 'Assessment status'}
            </h1>
            <p className="text-xs text-slate-500">
              Per-student status
              {audienceLabel && (
                <>
                  {' · '}
                  <span
                    className="inline-flex items-center gap-1 text-slate-700 font-medium"
                    data-testid="status-audience-label"
                  >
                    <Users size={11} />
                    {audienceLabel}
                  </span>
                </>
              )}
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate(`/school-teacher/assessments/${aid}/analytics`)} data-testid="open-analytics-btn">
            Analytics
          </Button>
          <Button variant="outline" size="sm" onClick={() => { load(); loadNeedsHelp(); }} disabled={loading} data-testid="refresh-btn">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-indigo-600" /></div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="status-kpis">
              {[
                { key: 'not_started', label: 'Not started' },
                { key: 'in_progress', label: 'In progress' },
                { key: 'submitted', label: 'Awaiting review' },
                { key: 'released', label: 'Released' },
              ].map((kpi) => (
                <div key={kpi.key} className="bg-white border rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-slate-900">{counts[kpi.key] || 0}</div>
                  <div className="text-xs text-slate-500 mt-1">{kpi.label}</div>
                </div>
              ))}
            </div>

            <div className="bg-white border rounded-xl overflow-hidden">
              <table className="w-full text-sm" data-testid="status-table">
                <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                  <tr>
                    <th className="text-left px-4 py-3">Student</th>
                    <th className="text-left px-4 py-3">Status</th>
                    <th className="text-left px-4 py-3">Score</th>
                    <th className="text-left px-4 py-3">Submitted</th>
                    <th className="text-right px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && (
                    <tr>
                      <td colSpan={5} className="text-center py-10 text-slate-500">
                        {isTargeted ? 'No targeted students.' : 'No students enrolled in this class.'}
                      </td>
                    </tr>
                  )}
                  {rows.map((r) => {
                    const pill = STATUS_PILL[r.status] || STATUS_PILL.not_started;
                    const nh = needsHelpByEnrollment[r.enrollment_id];
                    const topicCount = nh?.topics?.length || 0;
                    const badgeStyle = topicCount >= 2
                      ? 'bg-rose-50 text-rose-700 border-rose-400 hover:bg-rose-100'
                      : 'bg-amber-50 text-amber-800 border-amber-400 hover:bg-amber-100';
                    return (
                      <tr key={r.enrollment_id} className="border-t" data-testid={`status-row-${r.enrollment_id}`}>
                        <td className="px-4 py-3">
                          <div className="font-medium text-slate-900">{r.student_name || '—'}</div>
                          <div className="text-xs text-slate-500">{r.student_email}</div>
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant="outline" className={pill.cls}>{pill.label}</Badge>
                        </td>
                        <td className="px-4 py-3 text-slate-700">
                          {r.total_score != null
                            ? `${r.total_score}/${r.max_score} (${r.percent ?? 0}%)`
                            : '—'}
                        </td>
                        <td className="px-4 py-3 text-slate-500 text-xs">
                          {r.submitted_at ? new Date(r.submitted_at).toLocaleString() : '—'}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center gap-2 justify-end">
                            {(r.status === 'submitted' || r.status === 'released') && r.attempt_id && (
                              <Button
                                size="sm" variant="outline"
                                onClick={() => navigate(`/school-teacher/attempts/${r.attempt_id}`)}
                                data-testid={`open-attempt-${r.attempt_id}`}
                              >
                                <MessageSquare size={14} className="mr-1" /> Review
                              </Button>
                            )}
                            {topicCount > 0 && nh && (
                              <button
                                type="button"
                                onClick={() => openSheetForStudent(nh)}
                                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border-[1.5px] transition-all ${badgeStyle} ${topicCount >= 2 ? 'animate-pulse-subtle' : ''}`}
                                title={`Needs help in ${topicCount} topic${topicCount === 1 ? '' : 's'} — click to view`}
                                data-testid={`needs-help-badge-${r.enrollment_id}`}
                                aria-label={`Needs help in ${topicCount} topics`}
                              >
                                <Handshake size={12} />
                                <span>{topicCount}</span>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Class-wide gaps roll-up — collapsed, only shows topics with 2+
                students. Celebratory state when none. */}
            <ClassWideGapsRollup
              groups={classWideGroups}
              needsHelpByStudent={needsHelpByStudent}
              assessmentId={aid}
              assessmentTitle={a?.title || ''}
              defaultDurationMinutes={a?.duration_minutes || 20}
              onStudentClick={openSheetForStudent}
              onRemedialCreated={(newAid) => {
                navigate(`/school-teacher/assessments/${newAid}`);
              }}
            />
          </>
        )}
      </main>

      <StudentNeedsHelpSheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        student={activeStudent}
        assessmentId={aid}
        assessmentTitle={a?.title || ''}
        defaultDurationMinutes={a?.duration_minutes || 20}
        onRemedialCreated={(newAid) => {
          navigate(`/school-teacher/assessments/${newAid}`);
        }}
      />
    </div>
  );
}
